import { Markdown, markdownComponents } from "@commerce/components/Markdown";
import { Logo } from "@shared/components/Logo";
import { Link } from "@tanstack/react-router";
import { Badge } from "@ui/components/badge";
import { cn } from "@ui/lib";
import type { ComponentProps } from "react";
import type { Components } from "streamdown";
import { LEGAL_DOCUMENTS, type LegalDocument } from "./documents";

/**
 * One public legal document (privacy, terms, DPA, subprocessors): Hephon's
 * own page, outside the admin shell, readable at phone width.
 */
export function LegalPage({ document }: { document: LegalDocument }) {
	return (
		<div className="min-h-svh bg-background">
			<div className="mx-auto flex w-full max-w-2xl flex-col gap-8 px-4 pt-6 pb-16 md:pt-10">
				<Logo />
				<nav aria-label="Legal documents">
					<ul className="-mx-3 flex flex-wrap gap-1">
						{LEGAL_DOCUMENTS.map((doc) => (
							<li key={doc.slug}>
								<Link
									to="/legal/$slug"
									params={{ slug: doc.slug }}
									className="inline-flex h-9 items-center rounded-xl px-3 text-muted-foreground text-sm transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand data-[status=active]:bg-muted data-[status=active]:font-medium data-[status=active]:text-foreground"
								>
									{doc.label}
								</Link>
							</li>
						))}
					</ul>
				</nav>
				<article className="flex flex-col gap-6">
					<header className="flex flex-col gap-3">
						{document.draft ? (
							<Badge variant="warning" className="w-fit">
								Draft pending legal review
							</Badge>
						) : null}
						<h1 className="text-balance font-semibold text-2xl tracking-tight">
							{document.title}
						</h1>
						{document.updated ? (
							<p className="text-muted-foreground text-xs">
								Last updated {document.updated}
							</p>
						) : null}
					</header>
					<Markdown
						components={LEGAL_COMPONENTS}
						className="space-y-4 text-pretty text-sm leading-relaxed [&_li]:my-1.5 [&_ol]:list-outside [&_ol]:pl-5 [&_ul]:list-outside [&_ul]:pl-5"
					>
						{document.body}
					</Markdown>
				</article>
			</div>
		</div>
	);
}

type WithNode<T extends keyof React.JSX.IntrinsicElements> =
	ComponentProps<T> & { node?: unknown };

function Heading2({ node: _node, className, ...props }: WithNode<"h2">) {
	return (
		<h2
			className={cn("pt-4 font-semibold text-base", className)}
			{...props}
		/>
	);
}

function Heading3({ node: _node, className, ...props }: WithNode<"h3">) {
	return (
		<h3
			className={cn("pt-2 font-semibold text-sm", className)}
			{...props}
		/>
	);
}

/** Same tab: these links go between the legal pages or to an email address. */
function Anchor({
	node: _node,
	target: _target,
	rel: _rel,
	className,
	...props
}: WithNode<"a">) {
	return (
		<a
			className={cn(
				"font-medium text-foreground underline underline-offset-4 hover:decoration-2",
				className,
			)}
			{...props}
		/>
	);
}

// Module scope: Streamdown memoizes by prop identity.
const LEGAL_COMPONENTS: Components = {
	...markdownComponents,
	h2: Heading2,
	h3: Heading3,
	a: Anchor,
};
